'use client';

/**
 * PromptDetailModal — opens from a card click.
 *
 * Matches the design prototype's `.modal-*` chrome:
 *  - Two-column layout (1.1fr media, 1fr body) on desktop.
 *  - Left: cover image (or text-prompt preview fallback).
 *  - Right: author row + title + description + tags + prompt block + footer actions.
 *  - Footer: Like, Save, Remix, "Copy prompt". No comments for now.
 *  - Dark backdrop + blur + slide-up + escape-to-close + outside-click-to-close.
 *
 * Deliberately keeps its own save/copy state so the modal feels snappy — the
 * counts on the underlying card don't need to re-render while the modal is open.
 */

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useMemo, useState, type ReactElement, Fragment } from 'react';
import { createPortal } from 'react-dom';
import { Prompt } from '@/types';
import { useAuth } from '@/lib/auth-context';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import { copyToClipboard } from '@/lib/clipboard';
import { useAIModels } from '@/lib/ai-models-context';
import PromptToolbar from '@/components/prompt/PromptToolbar';
import SavePopover from '@/components/prompt/SavePopover';

/* Highlight {variables}, [VARIABLES], and leading KEYWORDS: blocks in the prompt text. */
function highlightPrompt(text: string) {
  const re = /(\{[^{}\n]{1,60}\}|\[[A-Z][A-Z0-9_]*\]|^[A-Z][A-Za-z ]+:(?=\s))/gm;
  const parts: (string | ReactElement)[] = [];
  let last = 0;
  let idx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const token = m[0];
    if (token.startsWith('{') || token.startsWith('[')) {
      parts.push(<span key={idx++} className="var">{token}</span>);
    } else {
      parts.push(<span key={idx++} className="kw">{token}</span>);
    }
    last = m.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.map((p, i) => <Fragment key={i}>{p}</Fragment>);
}

/* Category dot colors (match PromptCard). */
const CATEGORY_DOT: Record<string, string> = {
  Writing:   'oklch(0.75 0.14 160)',
  Coding:    'oklch(0.72 0.14 230)',
  Art:       'oklch(0.72 0.15 285)',
  Business:  'oklch(0.78 0.14 75)',
  Education: 'oklch(0.75 0.14 40)',
  Other:     'oklch(0.58 0.008 85)',
};

function fmtAgo(iso?: string): string {
  if (!iso) return 'just now';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 'just now';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.round(months / 12)}y ago`;
}

type Props = {
  prompt: Prompt;
  onClose: () => void;
  onSaveToggle?: (id: string, saved: boolean) => void;
};

export default function PromptDetailModal({ prompt, onClose, onSaveToggle }: Props) {
  const router = useRouter();
  const { user } = useAuth();
  const toast = useToast();
  const { models: aiModels } = useAIModels();

  const [isSaved, setIsSaved] = useState(!!prompt.is_saved);
  const [saveCount, setSaveCount] = useState(prompt.save_count ?? 0);
  const [copyCount, setCopyCount] = useState(prompt.copy_count ?? 0);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  // Translation state — null = original. Setting `translatedText` swaps the
  // rendered prompt block; the Copy button still copies the visible text so
  // the user gets whichever language they're looking at.
  const [translatedText, setTranslatedText] = useState<string | null>(null);
  const [translatedLang, setTranslatedLang] = useState<string | null>(null);

  /* Lock scroll + ESC-to-close + SSR-safe portal-mount flag. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(prompt.content ?? '');
    if (!ok) { toast.error('Couldn\u2019t copy to clipboard'); return; }
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
    // Best-effort server-side copy count bump — ignored if rate-limited.
    try {
      const res = await api.prompts.copy(prompt.id);
      if (typeof res?.copy_count === 'number') setCopyCount(res.copy_count);
    } catch { /* ignore */ }
    toast.success('Prompt copied');
  };

  const handleSave = async () => {
    if (!user) { router.push('/auth/login'); return; }
    if (busy) return;
    setBusy(true);
    try {
      if (isSaved) {
        await api.prompts.unsave(prompt.id);
        setIsSaved(false);
        setSaveCount((c) => Math.max(0, c - 1));
        onSaveToggle?.(prompt.id, false);
      } else {
        await api.prompts.save(prompt.id);
        setIsSaved(true);
        setSaveCount((c) => c + 1);
        onSaveToggle?.(prompt.id, true);
      }
    } catch {
      toast.error('Save failed');
    } finally {
      setBusy(false);
    }
  };

  const handleRemix = () => {
    if (!user) { router.push('/auth/login'); return; }
    router.push(`/create?fork_from=${prompt.id}`);
  };

  const goToFull = () => {
    router.push(`/prompt/${prompt.id}`);
  };

  const author = prompt.user;
  const aiModelById = useMemo(() => new Map(aiModels.map((m) => [m.id, m])), [aiModels]);
  const categoryKey = prompt.category || 'Other';
  const dotColor = CATEGORY_DOT[categoryKey] ?? CATEGORY_DOT.Other;
  // Build gallery: prefer images[] if present; fall back to cover_image_url
  const gallery: string[] =
    prompt.images && prompt.images.length > 0
      ? prompt.images
      : prompt.cover_image_url
      ? [prompt.cover_image_url]
      : [];
  const hasImage = gallery.length > 0;

  // Don't render during SSR — the portal target (document.body) doesn't exist.
  if (!mounted) return null;

  const modal = (
    <div
      className="ps-modal-backdrop"
      // Stop bubbling: the modal is rendered via a Portal, which means React
      // still bubbles synthetic events up to the parent component (the
      // PromptCard <article onClick={openDetail}>). Without this, every click
      // inside the modal would re-open it. Actual outside-click-to-close is
      // still handled by the `e.target === e.currentTarget` check.
      onClick={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget) onClose();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ps-modal-title"
    >
      <div className="ps-modal">
        <button
          type="button"
          className="ps-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          <span className="material-symbols-outlined">close</span>
        </button>

        {/* Media column */}
        <div className="ps-modal-media">
          {hasImage ? (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {/* Main image */}
              <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={gallery[galleryIndex]}
                  alt={prompt.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              {/* Thumbnail strip (only when >1 image) */}
              {gallery.length > 1 && (
                <div style={{
                  display: 'flex', gap: 4, padding: '6px 8px',
                  background: 'var(--bg-soft)',
                  overflowX: 'auto',
                  flexShrink: 0,
                }}>
                  {gallery.map((src, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setGalleryIndex(i); }}
                      style={{
                        width: 44, height: 44, flexShrink: 0,
                        borderRadius: 'var(--radius-xs)',
                        overflow: 'hidden',
                        border: i === galleryIndex
                          ? '2px solid var(--accent)'
                          : '2px solid transparent',
                        cursor: 'pointer',
                        padding: 0, background: 'none',
                        transition: 'border-color 0.12s',
                      }}
                      aria-label={`Image ${i + 1}`}
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Text-only prompts: render as a code-style preview centered both
            // horizontally and vertically inside the media column. Using grid
            // + place-items is the most reliable centering across browsers
            // (the older `justify-content: safe center` left text top-aligned
             // on mobile Chrome, which is what the user flagged).
            <div
              className="ps-modal-text-preview"
              style={{
                padding: '36px 40px',
                height: '100%',
                width: '100%',
                boxSizing: 'border-box',
                background: 'var(--bg-soft)',
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 14,
                  color: 'var(--ink-2)',
                  lineHeight: 1.75,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  // Cap line length so wide viewports keep a readable measure.
                  maxWidth: '60ch',
                  width: '100%',
                }}
              >
                {highlightPrompt(prompt.content ?? '')}
              </div>
            </div>
          )}

          {prompt.category && (
            <div className="ps-model-badge">
              <span className="model-dot" style={{ background: dotColor }} />
              {prompt.category}
            </div>
          )}
        </div>

        {/* Body column */}
        <div className="ps-modal-body">
          {/* Head */}
          <div className="ps-modal-head">
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.12em',
              color: 'var(--ink-3)', fontWeight: 600,
            }}>
              {prompt.category && <span>{prompt.category}</span>}
              {prompt.category && <span>·</span>}
              <span>{fmtAgo(prompt.created_at)}</span>
            </div>

            <h2 id="ps-modal-title">{prompt.title}</h2>

            {prompt.description && (
              <p className="ps-modal-desc">{prompt.description}</p>
            )}

            {author && (
              <div className="ps-modal-author">
                <Link
                  href={`/profile/${author.username}`}
                  onClick={(e) => e.stopPropagation()}
                  className="no-underline"
                  style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'inherit' }}
                >
                  {author.avatar_url ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={author.avatar_url}
                      alt={author.name}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', objectFit: 'cover',
                        border: '1px solid var(--line)',
                      }}
                    />
                  ) : (
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: 'linear-gradient(135deg, var(--accent), var(--accent-ink))',
                      color: 'white', display: 'grid', placeItems: 'center',
                      fontSize: 11, fontWeight: 600,
                    }}>
                      {author.name.slice(0, 1).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <div className="ps-modal-author-name">{author.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>@{author.username}</div>
                  </div>
                </Link>
              </div>
            )}
          </div>

          {/* Scrollable body */}
          <div className="ps-modal-scroll">
            {/* AI models "Works with" — each chip opens the dedicated model
                detail page at `/models/{id}` where the user gets brand info
                + every prompt designed for that model. */}
            {prompt.models && prompt.models.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{
                  fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.1em', color: 'var(--ink-3)',
                  marginBottom: 6,
                }}>
                  Works with
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                  {prompt.models.map((id) => {
                    const m = aiModelById.get(id);
                    const label = m?.name ?? id;
                    return (
                      <Link
                        key={id}
                        href={`/models/${id}`}
                        title={`Open ${label} details`}
                        onClick={onClose}
                        className="no-underline"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 9px',
                          fontSize: 11, fontWeight: 500,
                          borderRadius: 'var(--radius-full)',
                          background: m ? `${m.color}18` : 'var(--bg-soft)',
                          color: m?.color ?? 'var(--ink-2)',
                          border: `1px solid ${m?.color ?? 'var(--line)'}33`,
                          cursor: 'pointer',
                          transition: 'transform 0.12s ease, box-shadow 0.12s ease',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                      >
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: m?.color ?? 'var(--ink-3)', flexShrink: 0,
                        }} />
                        {label}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tags — each navigates to `/?tags=tagname`, the same URL the
                home page already understands for tag-filtered listings. */}
            {prompt.tags && prompt.tags.length > 0 && (
              <div className="ps-modal-tags">
                {prompt.tags.map((t) => (
                  <Link
                    key={t}
                    href={`/?tags=${encodeURIComponent(t)}`}
                    title={`Browse #${t} prompts`}
                    onClick={onClose}
                    className="ps-tag ps-tag-link no-underline"
                  >
                    #{t}
                  </Link>
                ))}
              </div>
            )}

            {/* Prompt block — toolbar shows char/token counts and a translate
                dropdown. Translating only swaps the rendered text; the Copy
                button copies whatever's visible (original or translation),
                which matches what most users expect. */}
            {(() => {
              const visible = translatedText ?? (prompt.content ?? '');
              return (
                <>
                  <div
                    className="ps-modal-label"
                    style={{ flexWrap: 'wrap', gap: 8 }}
                  >
                    <span>The prompt</span>
                    <PromptToolbar
                      text={visible}
                      charCount={visible.length}
                      activeLang={translatedLang}
                      onTranslated={(translated, lang) => {
                        setTranslatedText(translated);
                        setTranslatedLang(translated ? (lang ?? null) : null);
                      }}
                    />
                  </div>
                  <div className="ps-prompt-block">
                    <button
                      type="button"
                      className={`ps-prompt-copy-btn ${copied ? 'copied' : ''}`}
                      onClick={() => {
                        // Copy the VISIBLE text (translated if active).
                        copyToClipboard(visible).then(() => {
                          setCopied(true);
                          setTimeout(() => setCopied(false), 1600);
                        });
                      }}
                      aria-label="Copy prompt"
                    >
                      <span className="material-symbols-outlined">
                        {copied ? 'check' : 'content_copy'}
                      </span>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                    {highlightPrompt(visible)}
                  </div>
                </>
              );
            })()}

            {/* Stats row */}
            <div style={{
              display: 'flex', gap: 20, fontSize: 12, color: 'var(--ink-3)',
              marginTop: 6, flexWrap: 'wrap',
            }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>visibility</span>
                {(prompt.view_count ?? 0).toLocaleString()} views
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>bookmark</span>
                {saveCount.toLocaleString()} saved
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>content_copy</span>
                {copyCount.toLocaleString()} copied
              </span>
            </div>
          </div>

          {/* Footer actions */}
          <div className="ps-modal-foot">
            {/* SavePopover gives users the full collection picker (default
                Saved + per-collection checkboxes + new-collection inline
                form) instead of the bare bookmark toggle the modal had
                before — matched the card hover overlay's behavior. */}
            <SavePopover
              promptId={prompt.id}
              initialSaved={isSaved}
              variant="pill"
              align="left"
              onSavedChange={(s) => {
                setIsSaved(s);
                setSaveCount((c) => Math.max(0, c + (s ? 1 : -1)));
                onSaveToggle?.(prompt.id, s);
              }}
            />
            <button
              type="button"
              className="ps-btn ps-btn-secondary"
              onClick={handleRemix}
              title="Fork this prompt — opens the editor with a copy"
            >
              <span className="material-symbols-outlined ps-icon">fork_right</span>
              Remix
            </button>
            <button
              type="button"
              className="ps-btn ps-btn-ghost"
              onClick={goToFull}
              title="Open full page (for sharing, forks, etc.)"
              style={{ marginLeft: 'auto' }}
            >
              <span className="material-symbols-outlined ps-icon">open_in_new</span>
              Full page
            </button>
            <button
              type="button"
              className="ps-btn ps-btn-primary"
              onClick={handleCopy}
            >
              <span className="material-symbols-outlined ps-icon">
                {copied ? 'check' : 'content_copy'}
              </span>
              {copied ? 'Copied' : 'Copy prompt'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  // Portal to <body> — avoids `position: fixed` being trapped inside the
  // card's containing block (the card uses `transform` + `overflow: hidden`
  // on hover, which would otherwise clip + re-root the modal).
  return createPortal(modal, document.body);
}
